/* eslint-disable import/extensions */
/* eslint-disable class-methods-use-this */
/* eslint-disable import/no-unresolved */
import * as three from "three";
import { BehaviorSubject } from "rxjs";
import StageRaycaster from "./StageRaycaster";
import { vectorizeGenomicPos } from "../helpers/vectorize";
import clamp from "../../../common/helpers/clamp";
import StageCamera from "./StageCamera";

class StageScene {
  constructor(renderer0, renderer1) {
    this.pxRatio = clamp(Math.round(window.devicePixelRatio || 1), 1, 2);
    this.tracks = [];
    this.stackedTracks = [];
    this.overlayTracks = [];
    this.normalMeshes = [];
    this.allMeshes = [];

    this.renderer0 = renderer0;
    this.renderer1 = renderer1;

    this.cam0 = new StageCamera();
    this.cam1 = new StageCamera();
    this.cam2 = new StageCamera();

    this.baseScene = new three.Scene();
    this.antialiasScene = new three.Scene();

    this.scissorY = 0;
    this.scissorHeight = 0;

    this.scrollHeight = 0;
    this.elWidth = 0;
    this.elHeight = 0;
    this.topOffset = 0;

    this.raycaster = new StageRaycaster(this);
    this.isRaycastValid$ = new BehaviorSubject(false);
  }

  setTracks(tracks) {
    this.tracks = tracks;
    this.stackedTracks = tracks.filter((x) => !x.isOverlay);
    this.overlayTracks = tracks.filter((x) => x.isOverlay);
    this.allMeshes = [].concat(...this.tracks.map((t) => t.meshes));
    this.allMeshes.forEach((x) => this.setScene(x));
  }

  setSize(width, height, isTop) {
    this.elWidth = width;
    this.elHeight = height;

    const scissorY = isTop ? height - this.scrollHeight : 0;
    const scissorHeight = isTop ? this.scrollHeight : height - this.topOffset;
    if (scissorHeight < 1) return;

    this.raycaster.setSize(this.elWidth, this.elHeight, this);
    this.renderer0.setSize(this.elWidth, this.elHeight);
    this.renderer1.setSize(this.elWidth, this.elHeight);

    this.renderer0.setPixelRatio(Math.round(window.devicePixelRatio || 1));
    this.renderer1.setPixelRatio(Math.round(window.devicePixelRatio || 1));

    this.scissorHeight = scissorHeight;
    this.scissorY = scissorY;
  }

  setScene(mesh) {
    if (mesh.material.uniforms.u_antialias?.value) {
      mesh.setScene(this.antialiasScene);
    } else {
      mesh.setScene(this.baseScene);
    }
    return mesh;
  }

  setZoom(val) {
    const { rangeDist, d2, d1 } = val;
    const uxDist = d2 - d1;
    const vD1 = vectorizeGenomicPos(d1);
    const uxStart = new three.Vector2(...vD1);
    for (let i = 0; i < this.allMeshes.length; i++) {
      const mesh = this.allMeshes[i];
      mesh.setIsInverted(uxDist < 0);
      mesh.setPxRatio(this.pxRatio);
      mesh.setWidth(rangeDist);
      mesh.setXStart(uxStart);
      mesh.setXDist(uxDist);
    }
  }

  setIsRaycasting(val) {
    for (let i = 0; i < this.allMeshes.length; i++) {
      const mat = this.allMeshes[i].mesh.material;
      mat.uniforms.u_is_raycasting.value = val;
    }
  }

  setXFrustum(start, end) {
    this.cam0.setXFrustum(start, end);
    this.cam1.setXFrustum(start, end);
    this.cam2.setXFrustum(start, end);
  }

  scroll(scrollOffset) {
    const scrollHeight = this.elHeight - this.topOffset;
    const percent = scrollHeight / this.scrollHeight;
    const scrollPercentage = percent > 1 ? 1 : percent;
    const y0 = scrollOffset / this.scrollHeight;
    const y1 = Math.min(1, y0 + scrollPercentage);

    this.cam0.setYFrustum(y0, y1);
    this.cam1.setYFrustum(y0, y1);
    this.cam2.setYFrustum(y0, y1);
  }

  setTopOffset(offset) {
    this.raycaster.topOffset = offset;
    this.topOffset = offset;
  }

  adjustTrackHeights() {
    const totalHeight = this.stackedTracks.reduce((offset, x) => {
      x.updateProjectionMatrix(offset, this.scrollHeight, this.topOffset);
      return offset + x.calcHeight();
    }, 0);

    this.overlayTracks.forEach((track) => {
      track.meshes.forEach((x) => x.setHeight(totalHeight));
    });

    return this;
  }

  calcTotalHeight() {
    this.scrollHeight = this.tracks
      .filter((x) => !x.isOverlay)
      .reduce((a, i) => a + i.calcHeight(), 0);
    return this;
  }

  renderRaycast() {
    this.raycaster.render(
      this.allMeshes,
      this.baseScene,
      this.renderer1,
      this.cam1.camera
    );
  }

  raycast(meshes, x, y) {
    if (!this.isRaycastValid$.value) {
      this.renderRaycast();
      this.isRaycastValid$.next(true);
    }
    return this.raycaster.raycast(meshes, x, this.elHeight - y);
  }

  dispose() {
    this.tracks.forEach((x) => x.dispose());
    this.raycaster.dispose();
  }

  render() {
    const sy = this.scissorY;
    const sh = this.scissorHeight;
    const w = this.elWidth;

    this.renderer0.setScissor(0, sy, w, sh);
    this.renderer0.setViewport(0, sy, w, sh);
    this.renderer1.setScissor(0, sy, w, sh);
    this.renderer1.setViewport(0, sy, w, sh);

    if (this.isRaycastValid$.value) {
      this.isRaycastValid$.next(false);
    }

    this.renderer0.render(this.antialiasScene, this.cam0.camera);
    this.renderer1.render(this.baseScene, this.cam1.camera);
  }
}

export default StageScene;
